import { z } from 'zod/v3';
import { CocoEvent } from '@cotera/client/app/etc';
import { v4 } from 'uuid';
import { err, ok } from 'neverthrow';
import { BroadcastEventType } from '@cotera/api';
import {
  ActionResult,
  ActionTrackMetadata,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import type { ActionTrackingContext } from '@cotera/client/v0/actions/framework/action-tracking';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { toast } from '@cotera/client/app/components/ui';
import { emit } from '@cotera/client/app/etc/broadcast-event-bus';
import type { WebhookProviderConfigDraft } from '@cotera/client/app/v1/webhook-providers';
import { WorkflowCronTriggerNodeModel } from '../workflow/workflow-node-models/workflow-cron-trigger-node.model';
import { WorkflowWebhookTriggerFullNodeModel } from '../workflow/workflow-node-models/workflow-webhook-trigger-full-node.model';
import { WorkflowWebhookTriggerPreviewNodeModel } from '../workflow/workflow-node-models/workflow-webhook-trigger-preview-node.model';

export type TriggerFireKind = 'cron' | 'webhook' | 'event' | 'dataset';

export type CreateStandaloneTriggerParams = {
  fires: TriggerFireKind;
  name: string;
  folderId?: string;
  schedule: string;
  eventName: string;
  webhook: WebhookProviderConfigDraft;
};

function emitTriggerWorkflowCreated(orgId: string, workflowId: string): void {
  emit(BroadcastEventType.ResourceUpdated, {
    orgId,
    resourceType: 'workflow',
    resourceId: workflowId,
  });
}

export class CreateStandaloneTriggerAction extends BaseAction<
  CreateStandaloneTriggerParams,
  { t: string; message?: string }[]
> {
  title = 'Create trigger';
  description = 'Create a standalone trigger workflow';
  inputSchema = z.object({
    fires: z.enum(['cron', 'webhook', 'event', 'dataset']),
    name: z.string(),
    folderId: z.string().uuid().optional(),
    schedule: z.string(),
    eventName: z.string(),
    webhook: z.custom<WebhookProviderConfigDraft>(),
  });
  group = 'Trigger';
  icon = 'plus';

  private _lastCreatedWorkflowId: string | null = null;

  constructor(
    private readonly client: TenantedClient,
    private readonly orgId: string,
    private readonly trackEvent: (
      event: string,
      properties: Record<string, unknown>
    ) => void
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    if (context.hasTargetedModelOfType(WorkflowCronTriggerNodeModel)) {
      return false;
    }
    if (context.hasTargetedModelOfType(WorkflowWebhookTriggerFullNodeModel)) {
      return false;
    }
    if (
      context.hasTargetedModelOfType(WorkflowWebhookTriggerPreviewNodeModel)
    ) {
      return false;
    }
    return true;
  }

  async execute(
    input: CreateStandaloneTriggerParams,
    _context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const { fires, folderId, schedule } = input;
    const name = input.name.trim();
    const eventName = input.eventName.trim();

    // A new trigger creates a workflow with only the trigger node — no run step
    // attached. The user wires up what it runs afterwards in the graph editor.
    //
    // `addTrigger` mints the workflow (at this id, in `folderId`) in the same
    // request that creates the trigger + binding. Creating the workflow in a
    // separate `workflow.create` call first leaves it briefly bound to nothing,
    // which the Automations page surfaces under "Standalone Workflows" until the
    // bind lands — so the create is atomic server-side, and a failed trigger
    // rolls the workflow back there too (no client-side compensation needed).
    const workflowId = v4();
    const folder = folderId !== undefined ? { folderId } : {};

    if (fires === 'cron') {
      const triggerRes = await this.client.resource.workflow.addTrigger({
        kind: 'cron',
        workflowId,
        name,
        ...folder,
        schedule,
        timezone: 'UTC',
      });
      if (triggerRes.isErr()) {
        toast.error('Failed to schedule the trigger');
        return err([{ t: 'error', message: 'Failed to schedule the trigger' }]);
      }
      this.trackEvent(CocoEvent.ScheduledToolWorkflowCreated, { workflowId });
    } else if (fires === 'event') {
      const triggerRes = await this.client.resource.workflow.addTrigger({
        kind: 'event',
        workflowId,
        name,
        ...folder,
        eventName,
      });
      if (triggerRes.isErr()) {
        toast.error('Failed to create the event trigger');
        return err([
          { t: 'error', message: 'Failed to create the event trigger' },
        ]);
      }
      this.trackEvent(CocoEvent.WebhookWorkflowCreated, {
        workflowId,
        fires: 'event',
      });
    } else {
      const { webhook } = input;
      const triggerRes = await this.client.resource.workflow.addTrigger({
        kind: 'webhook',
        workflowId,
        name,
        ...folder,
        providerKey: webhook.providerKey,
        eventKey: webhook.eventKey ?? undefined,
        eventConfig: webhook.eventConfig,
        verificationConfig: webhook.verificationConfig,
      });
      if (triggerRes.isErr()) {
        toast.error('Failed to create the webhook');
        return err([{ t: 'error', message: 'Failed to create the webhook' }]);
      }
      this.trackEvent(CocoEvent.WebhookWorkflowCreated, {
        workflowId,
        providerKey: webhook.providerKey,
      });
    }

    this._lastCreatedWorkflowId = workflowId;
    emitTriggerWorkflowCreated(this.orgId, workflowId);
    return ok({ resourceId: workflowId });
  }

  track({
    payload,
  }: ActionTrackingContext<
    CreateStandaloneTriggerParams,
    { t: string; message?: string }[]
  >): ActionTrackMetadata | null {
    if (this._lastCreatedWorkflowId === null) {
      return null;
    }
    return {
      event:
        payload.fires === 'cron'
          ? CocoEvent.ScheduledToolWorkflowCreated
          : CocoEvent.WebhookWorkflowCreated,
      properties: { workflowId: this._lastCreatedWorkflowId },
    };
  }
}
